import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import { isEmpty, isNull, isBoolean } from 'lodash';
import moment from 'moment';
import styled from '@emotion/styled';
import { SectionSeparator } from '@styles/layout';
import { breakpoints, mq } from '@styles/breakpoints';
import { Theme, ThemeProps } from '@theme/base';
import { sendToursDataLayer } from '../../middleware/data-layer-middleware';
import { TourAvailabilityList, TourAvailabilityResult, TourContent, ItineraryType } from '@model/tours';
import { PageProps, TourPageSections } from '@model/pages';
import { Itinerary, Name, Place } from '@model/iceberg';
import { PageType } from '@model/analytics/data-layer-payload-builder';
import { BookingSteps, Icons, State, TripTypes, Price } from '@model/common';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { FILTER_DEFAULTS } from '@model/price-calendar';
import { Occupancy } from '@model/state/deal-finder-state';
import { ERROR_CODES } from '@model/common/error-codes';
import { addRecentlyViewed, RecentlyViewedType } from '@state/slices/recently-viewed';
import { setTripTypeFilter } from '@state/slices/navigation';
import {
  fetchTourAvailability,
  fetchTourDossiers,
  getTourAvailability,
  getTourAvailabilityLoading,
  getTourDossiers,
  getTourDossiersLoading,
  getTourFilters,
  setTourFilters,
  setTourSummary,
  unsetTourReferences
} from '@state/slices/tours';
import { setTourLandingSearch } from '@state/slices/search/tours';
import { clearSearchResultsTours, getToursSearchToken } from '@state/slices/search-results/tours';
import { getDestinationNameFromPlace } from '@util/destination';
import { getImageUrl } from '@util/images';
import { getTourSummary, validateAdults } from '@util/tours';
import { Head } from '@components/common/head';
import { NavBarContainer } from '@components/navigation';
import { ProductHero } from '@components/common/hero/product/ProductHero';
import { DestinationBreadcrumb } from '@components/breadcrumbs';
import { ContentLayout } from '@components/common/layout';
import { TextDescriptionFade } from '@components/common/description';
import { Snackbar } from '@components/common/snackbar';
import { AlternativeTourPanel } from '@components/tours';
import { Footer } from '@components/footer';
import { TourProductDescription } from '@components/product/tour-product-description/TourProductDescription';
import { ExpandableBulletPointList } from '@components/common/list/bullet-point';
import { ItineraryTimeLine } from '@components/common/timeline/itinerary/ItineraryTimeLine';
import { ToursExtensionGroup } from '@components/common/card/tour-extension/TourExtensionGroup';
import { TourInformation } from '@components/product/tour-information';
import { AccommodationList } from '@components/accommodation/AccommodationList';
import { PageTabNavigation } from '@components/common/page-tab-navigation';
import { HotelInformationPanel, TourMap } from '@components/product';
import { TourReviewGroup } from '@components/product/tour-reviews';
import { SpecialOffer } from '@components/product/special-offers';
import { Error404, Error500 } from '@components/common/hero/error';
import { useI18NextContext } from '@components/hooks';
import { TourBasketPanel } from '@components/basket/v1/tours-basket-panel/ToursBasketPanel';
import SearchTabsBar from '@components/search/SearchTabsBar';
import { ToursStepperContainer } from '@components/common/stepper';
import { performTourRetargeting } from '@util/retargeting';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { TourDossiers } from '@components/product/tour-dossiers/TourDossiers';
import { TourPricingTabs } from '@components/product/tour-pricing';
import { SectionTitle } from '@components/common';
import { MOCK_TOUR_AVAILABILITY_LIST } from '@mock/iceberg/tours';
import { WhatsIncluded } from '@components/product/whats-included';
import { dispatch } from '@state/store';

const DynamicTourPricingWrapper = dynamic(
  () => import('../../components/product/tour-pricing/TourPricingWrapper').then((e) => e.TourPricingWrapper),
  {
    ssr: false
  }
);

const DynamicMultiCentrePricingWrapper = dynamic(
  () =>
    import('../../components/product/tour-pricing/MultiCentrePricingWrapper').then((e) => e.MultiCentrePricingWrapper),
  {
    ssr: false
  }
);

export interface ToursProductPageLayoutProps extends PageProps {
  theme: Theme;
  tourContent: TourContent;
  itinerary: Array<Itinerary>;
  tripType: TripTypes;
  place: Place;
  destinationPath: string;
  productPath: string;
  month?: string;
  adults?: string;
  airports?: Array<string>;
  departureDate: string;
  errorCode?: number;
  searchToken?: string;
}

const Container = styled.div({});

const MobileDossiers = styled.div({
  display: 'block',
  [mq.medium]: {
    display: 'none'
  }
});

const MapContainer = styled.div();

const PricePanelNotes = styled(Snackbar)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.large,
  p: {
    margin: 0
  }
}));

const TitleContainer = styled.div(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.medium
}));

export const ToursProductPageLayout = ({
  tourContent,
  place,
  destinationPath,
  productPath,
  tripType,
  theme,
  itinerary,
  month,
  departureDate,
  adults,
  airports = [],
  errorCode,
  promoBannerTripType,
  searchToken: querySearchToken
}: ToursProductPageLayoutProps) => {
  const t: any = useI18NextContext();
  const [hasRetargeted, setHasRetargeted] = useState(false);

  if (isEmpty(tourContent)) {
    if (errorCode && errorCode === 404) {
      return <Error404 code={ERROR_CODES.NO_TOURS} />;
    }
    return <Error500 code={ERROR_CODES.NO_TOURS} />;
  }
  const tourAvailability = useSelector(getTourAvailability);
  const tourAvailabilityLoading = useSelector(getTourAvailabilityLoading);
  const tourDossiers = useSelector(getTourDossiers);
  const tourDossiersLoading = useSelector(getTourDossiersLoading);
  const authToken = new Cookies().get(Cookie.TRADE_AUTHORIZATION);
  const searchToken = useSelector(getToursSearchToken);
  const { adults: selectedAdults } = useSelector(getTourFilters);
  const mockTourList: TourAvailabilityList = MOCK_TOUR_AVAILABILITY_LIST;

  const isPrivateTours: boolean = tripType === TripTypes.PRIVATE_TOURS;
  const isMultiCentre: boolean = tripType === TripTypes.MULTI_CENTRE;
  const showDailyPriceCalendar: boolean = isPrivateTours || isMultiCentre || tripType === TripTypes.FLY_DRIVES;
  const hasSearchToken: boolean = !!querySearchToken;

  let selectedPrice: Price | undefined = undefined;
  if (departureDate) {
    const [year] = departureDate.split('-');
    const availabilityYear = tourAvailability?.availability[0].results[year];
    const monthString = moment(departureDate).format('MMM');
    const availabilityMonth = availabilityYear && availabilityYear[0].months[monthString];
    selectedPrice = availabilityMonth?.find(({ departureDate: tourDate }) => tourDate.startsWith(departureDate))?.price;
  }

  const {
    alternativeTour,
    description,
    excursionsCount,
    extensions,
    fitHotels,
    gallery,
    highlights,
    hotels,
    imageUrl,
    information,
    linkedTours,
    mealsCount,
    name,
    offers,
    pace,
    price,
    pricePanelNotes,
    reviews,
    privateTour,
    soloTour,
    subTitle,
    type,
    vessels,
    whatsIncluded
  } = tourContent;

  const heroImageUrl = getImageUrl(imageUrl, breakpoints.large);
  const heroRef: any = useRef(null);
  const tourMapUrl = information?.map?.url;
  const occupancy: Array<Occupancy> =
    adults && validateAdults(adults)
      ? [{ adults: Number(adults), children: [] }]
      : tripType === TripTypes.SOLO_TOURS
        ? [{ adults: 1, children: [] }]
        : FILTER_DEFAULTS.occupancy;

  const destination: Name = {
    display: getDestinationNameFromPlace(place),
    path: destinationPath
  };

  const hasTourAvailability =
    !isNull(tourAvailability) && !!tourAvailability.availability && !isEmpty(tourAvailability.availability);

  const findAlternativeItinerary = (availability: Array<TourAvailabilityResult>, type: ItineraryType) =>
    !!availability.find(({ results }) =>
      Object.values(results).find((year) =>
        year.find(({ months }) =>
          Object.values(months).find((prices) =>
            prices.find(({ alternativeItinerary }) => {
              return !isBoolean(alternativeItinerary) && alternativeItinerary.type === type;
            })
          )
        )
      )
    );

  const hasAlternativeItinerary =
    hasTourAvailability && findAlternativeItinerary(tourAvailability.availability, ItineraryType.ALTERNATIVE);

  const hasReverseItinerary =
    hasTourAvailability && findAlternativeItinerary(tourAvailability.availability, ItineraryType.REVERSE);

  useEffect(() => {
    dispatch(setTourSummary(getTourSummary(tourContent, destination)));
    dispatch(
      sendToursDataLayer({
        tripType,
        pageType: PageType.TOUR_PRODUCT,
        connectId: tourContent.connectId
      })
    );

    dispatch(
      addRecentlyViewed({
        id: productPath,
        type: RecentlyViewedType.TOURS,
        tripType
      })
    );

    dispatch(setTripTypeFilter(tripType || TripTypes.ALL));
    dispatch(unsetTourReferences());
    if (!hasSearchToken) {
      dispatch(setTourLandingSearch(true));
      if (tripType) {
        dispatch(clearSearchResultsTours());
      }
    }

    const adultsValid = validateAdults(adults);

    dispatch(
      fetchTourAvailability({
        path: productPath,
        tripType: tripType,
        authToken,
        searchToken,
        ...(adultsValid ? { adults } : undefined),
        ...(airports && airports.length > 0 && { airport: airports[0] || undefined })
      })
    );

    dispatch(
      fetchTourDossiers({
        path: productPath,
        authToken,
        privateTour: isPrivateTours
      })
    );
  }, [productPath]);

  useEffect(() => {
    if (hasSearchToken) {
      if (
        !hasRetargeted &&
        productPath &&
        tourContent &&
        tourAvailability?.cheapest &&
        tourAvailability?.availability &&
        tourAvailability?.availability.length &&
        tourAvailability?.availability[0].variation.path === productPath
      ) {
        performTourRetargeting({
          path: productPath,
          tour: tourContent,
          cheapest: tourAvailability?.cheapest,
          price: selectedPrice,
          adults,
          departure: departureDate
        });
        setHasRetargeted(true);
      }
    }
  }, [productPath, tourContent, tourAvailability]);

  /* *** LOGIC *** */
  const scrollToPricing = () => {
    const pricingElement = anchorRefs[TourPageSections.PRICING].current;
    if (pricingElement) {
      window.scrollTo({
        top: pricingElement.offsetTop - theme!.custom.contentSizes.tabNavHeight,
        behavior: 'smooth'
      });
    }
  };

  /* *** REFS *** */
  const anchorRefs: any = Object.entries(TourPageSections).reduce((acc: any, [_, label]: any) => {
    acc[label] = useRef(null);
    return acc;
  }, {});

  const metaData = {
    tour: name,
    destination: destination.display
  };
  const title: string = t('seo__tour-itinerary--title', metaData);
  const metaDescription: string = t('seo__tour-itinerary--description', metaData);

  const onChangeFilters = ({ airport, adults }: { airport: string; adults: string }) => {
    const query = {
      searchToken,
      tripType,
      authToken,
      path: productPath,
      airport,
      adults
    };

    dispatch(setTourFilters({ airport, adults: Number(adults) }));
    dispatch(fetchTourAvailability(query));
  };

  return (
    <Container>
      <PageTabNavigation anchorRefs={() => anchorRefs} triggerRef={() => heroRef} />
      <Head metaTitle={title} metaDescription={metaDescription} />
      <NavBarContainer promoBannerTripType={promoBannerTripType} renderSearch={<SearchTabsBar condensed />} />
      {hasSearchToken && <ToursStepperContainer currentStep={BookingSteps.TOUR_DETAILS} tripType={tripType} />}
      {!!gallery?.length && heroImageUrl && (
        <div ref={heroRef}>
          <ProductHero hero={{ url: heroImageUrl, title: name }} gallery={gallery} />
        </div>
      )}
      {!hasSearchToken && (
        <DestinationBreadcrumb
          destination={destination}
          tour={{ display: name, path: productPath }}
          tripType={tripType}
        />
      )}
      <SectionSeparator size={theme.custom.spacing.xxLarge} />
      <ContentLayout
        maxWidth
        main={
          <>
            <TourProductDescription
              name={name}
              subTitle={subTitle}
              country={destination.display}
              continent={place?.continent.display}
              pace={pace}
              mealsCount={mealsCount}
              excursionsCount={excursionsCount}
            />
            <TextDescriptionFade description={description} />
            <SectionSeparator />
            <MobileDossiers>
              {!!tourDossiers?.length && tourDossiers && !tourDossiersLoading ? (
                <>
                  <TourDossiers dossiers={tourDossiers} />
                  <SectionSeparator />
                </>
              ) : tourDossiersLoading ? (
                <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} />
              ) : null}
            </MobileDossiers>
            {!!highlights?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.HIGHLIGHTS]} />
                <ExpandableBulletPointList items={highlights} title={'tour-product__title--tour-highlights'} />
                <SectionSeparator />
              </>
            )}
            {tourMapUrl && (
              <MapContainer>
                <TourMap tourMapUrl={tourMapUrl} tourName={name} />
                <SectionSeparator />
              </MapContainer>
            )}
            {!!itinerary?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.ITINERARY]} />
                <ItineraryTimeLine heading={'common__label--itinerary'} items={itinerary} />
                <SectionSeparator />
              </>
            )}
            {isMultiCentre && !!hotels?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.ACCOMMODATION]} />
                <AccommodationList items={hotels} />
                <SectionSeparator />
              </>
            )}
            {isMultiCentre && !!fitHotels?.length && (
              <>
                {!hotels?.length && <a ref={anchorRefs[TourPageSections.ACCOMMODATION]} />}
                <HotelInformationPanel hotels={fitHotels} />
              </>
            )}
            {whatsIncluded && whatsIncluded.length && (
              <>
                <SectionTitle heading={'whats-included__header'} />
                <WhatsIncluded whatsIncluded={whatsIncluded} />
                <SectionSeparator />
              </>
            )}
            {!!offers?.length && (
              <>
                <SpecialOffer specialOffer={offers} heading={'hotel__page-section--special-offers'} theme={theme} />
                <SectionSeparator />
              </>
            )}
            {!hasTourAvailability && tourAvailabilityLoading && (
              <>
                <TitleContainer>
                  <SectionTitle heading={t('common__label--dates-and-prices')} />
                </TitleContainer>
                {!showDailyPriceCalendar && (
                  <TourPricingTabs
                    availabilityResult={mockTourList.availability[0]}
                    tripType={tripType}
                    tourName={name}
                    month={month}
                    startDate={departureDate}
                    searchToken={searchToken}
                    airports={airports}
                    occupancy={occupancy}
                    onChangeFilters={onChangeFilters}
                    isGhosting={tourAvailabilityLoading}
                    cheapest={mockTourList.cheapest}
                  />
                )}
                {showDailyPriceCalendar && (
                  <TourPricingTabs
                    availabilityResult={mockTourList.availability[0]}
                    tripType={tripType}
                    tourName={name}
                    month={month}
                    startDate={departureDate}
                    searchToken={searchToken}
                    airports={airports}
                    occupancy={occupancy}
                    onChangeFilters={onChangeFilters}
                    isGhosting={tourAvailabilityLoading}
                    cheapest={mockTourList.cheapest}
                    showCalendar
                  />
                )}
                <SectionSeparator />
              </>
            )}
            {!isNull(tourAvailability) && hasTourAvailability && (
              <>
                <TitleContainer>
                  <SectionTitle heading={t('common__label--dates-and-prices')} />
                </TitleContainer>

                <a ref={anchorRefs[TourPageSections.PRICING]} />
                {!showDailyPriceCalendar && (
                  <DynamicTourPricingWrapper
                    tourAvailability={tourAvailability}
                    tripType={tripType}
                    tourName={name}
                    month={month}
                    departureDate={departureDate}
                    searchToken={searchToken}
                    airports={airports}
                    occupancy={occupancy}
                    productPath={productPath}
                    onChangeFilters={onChangeFilters}
                    isGhosting={tourAvailabilityLoading}
                  />
                )}
                {showDailyPriceCalendar && (
                  <DynamicMultiCentrePricingWrapper
                    tourAvailability={tourAvailability}
                    tripType={tripType}
                    tourName={name}
                    month={month}
                    departureDate={departureDate}
                    searchToken={searchToken}
                    airports={airports}
                    occupancy={occupancy}
                    productPath={productPath}
                    onChangeFilters={onChangeFilters}
                    isGhosting={tourAvailabilityLoading}
                  />
                )}
                {!!pricePanelNotes?.length && (
                  <PricePanelNotes icon={Icons.INFO} state={State.INFO} text={pricePanelNotes.join(', ')} />
                )}
                {hasAlternativeItinerary && (
                  <PricePanelNotes
                    icon={Icons.INFO}
                    state={State.INFO}
                    text={t('tour-product__header--alternativeItinerary')}
                  />
                )}
                {hasReverseItinerary && (
                  <PricePanelNotes
                    icon={Icons.INFO}
                    state={State.INFO}
                    text={t('tour-product__header--reverseItinerary')}
                  />
                )}
                {!!privateTour && (
                  <AlternativeTourPanel
                    alternativeTour={{ ...privateTour, tripType: TripTypes.PRIVATE_TOURS }}
                    fromTripType={tripType}
                    adults={selectedAdults || Number(adults)}
                  />
                )}
                {!!alternativeTour && (
                  <AlternativeTourPanel
                    alternativeTour={alternativeTour}
                    fromTripType={tripType}
                    adults={selectedAdults || Number(adults)}
                  />
                )}
                {!!soloTour && (
                  <AlternativeTourPanel
                    alternativeTour={soloTour}
                    fromTripType={tripType}
                    adults={selectedAdults || Number(adults)}
                  />
                )}
                {linkedTours?.map((tour) => (
                  <AlternativeTourPanel
                    key={tour.name}
                    alternativeTour={tour}
                    fromTripType={type}
                    adults={selectedAdults || Number(adults)}
                  />
                ))}
                <SectionSeparator />
              </>
            )}
            {!!vessels?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.VESSELS]} />
                <AccommodationList items={vessels} heading={'vessel-info-group__heading--title'} hideNotice />
                <SectionSeparator />
              </>
            )}
            {!isMultiCentre && !!hotels?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.ACCOMMODATION]} />
                <AccommodationList items={hotels} />
                <SectionSeparator />
              </>
            )}
            {!!reviews?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.REVIEWS]} />
                <TourReviewGroup reviews={reviews} heading={'tour__reviews--title'} />
                <SectionSeparator />
              </>
            )}
            {!!extensions?.length && (
              <>
                <a ref={anchorRefs[TourPageSections.EXTENSIONS]} />
                <ToursExtensionGroup
                  tripType={tripType}
                  extensions={extensions}
                  heading={'tour-product__title--extend-your-tour'}
                />
                <SectionSeparator />
              </>
            )}
            <a ref={anchorRefs[TourPageSections.IMPORTANT_INFO]} />
            <TourInformation tourInformation={information} heading={'common__label--important-information'} />
          </>
        }
        sidebar={
          <TourBasketPanel
            top={theme.custom.contentSizes.tabNavHeight + theme.custom.spacing.large}
            price={selectedPrice || tourAvailability?.cheapest?.price || price}
            onClick={scrollToPricing}
            dossiers={tourDossiers}
            dossiersGhosting={tourDossiersLoading}
            isGhosting={tourAvailabilityLoading || !tourAvailability}
          />
        }
      />
      <SectionSeparator />
      <Footer />
    </Container>
  );
};
